import React, { useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type SortableItemProps = {
  id: string;
  children: React.ReactNode;
};

const SortableItem: React.FC<SortableItemProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // By default, dnd-kit's `onKeyDown` listener intercepts "Space" key presses on buttons and inputs.
      // This breaks keyboard accessibility for buttons and prevents typing spaces in inputs.
      // Adding the `data-prevent-dnd-keydown` attribute disables dnd-kit's `onKeyDown` handler
      // for the element and all its descendants.
      if (e.target instanceof Element && e.target.matches('[data-prevent-dnd-keydown], [data-prevent-dnd-keydown] *')) {
        return;
      }

      // Call original
      listeners?.onKeyDown(e);
    },
    [listeners]
  );

  const overriddenListener = useMemo(() => ({ ...listeners, onKeyDown: handleKeyDown }), [handleKeyDown]);

  const style = {
    transform: CSS.Transform.toString(
      transform
        ? {
            ...transform,
            scaleY: 1,
            scaleX: 1
          }
        : null
    ),
    transition
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...overriddenListener}>
      {children}
    </div>
  );
};

export default SortableItem;
